import { cn } from '../lib/cn'
import { IconCheck } from '../lib/icons'

/* A rule that is not yet satisfied is not a failure: while someone is still typing, a red
 * error on every keystroke is noise. Unmet reads as neutral, met ticks — never destructive. */

export interface Requirement {
  /** The rule, already translated by the app's message layer. */
  readonly label: string
  readonly met: boolean
}

export interface RequirementListProps {
  /** Names the set for screen readers; it is not painted. */
  readonly label: string
  readonly requirements: readonly Requirement[]
  readonly className?: string
}

export function RequirementList({ label, requirements, className }: RequirementListProps) {
  return (
    <ul aria-label={label} className={cn('flex flex-col gap-1', className)}>
      {requirements.map((requirement) => (
        <RequirementItem key={requirement.label} {...requirement} />
      ))}
    </ul>
  )
}

function RequirementItem({ label, met }: Requirement) {
  return (
    <li
      data-met={met}
      className={cn(
        'flex items-center gap-2 text-note',
        met ? 'text-success-text' : 'text-muted-foreground',
      )}
    >
      <RequirementMark met={met} />
      {label}
    </li>
  )
}

// The tick is decorative: `data-met` on the row already carries the state, and the row's own
// text is the accessible name — an icon with a label would say every rule twice.
function RequirementMark({ met }: { met: boolean }) {
  if (!met) return <span aria-hidden="true" className="size-4 shrink-0" />
  return <IconCheck aria-hidden="true" className="size-4 shrink-0" />
}
